/**
 * 진화 기록 — 사용하면서 배운 것(docs/features/24-experience-evolution.md, 공통 원칙 A-9).
 *
 * 앱은 따로 가르치지 않아도 일상적인 사용에서 배운다(되풀이한 지시 · 대화에서 밝힌 선호 · 기계가 검증한 결과).
 * 이 창은 그 내역을 **언제든** 확인하고 고치는 자리다 — 매 결과마다 알리지 않는 대신 여기서 전부 보인다.
 *
 *   · 적용 중 — 지금 AI 요청에 실리는 것. 끄거나, 글을 고치거나(선호), 버린다
 *   · 관찰 중 — 아직 근거가 모자라 실리지 않는 것(같은 지시를 몇 번 더 하면 적용된다). 바로 켤 수 있다
 *   · 멈춤 — 내가 고친 가이드가 같은 주제를 정해서 멈춘 학습(직접 정한 것이 앞선다). 다시 켤 수 있다
 *   · 버림 — 다시 올라오지 않게 기억해 둔 것
 *   · 제안 — 쌓인 근거에서 나온 「기준을 이렇게 바꾸면 어떤가」(사례로 남기기 · 처방 순서 · 가이드 예외 · 분류 추가).
 *     기준은 사용자가 바꾼다 — 여기서는 그 편집 화면을 열 뿐이다. 판정은 기계가 하고, 가이드 예외의 글 초안만 AI 가 거든다(누를 때만)
 *
 * 직접 적는 선호도 여기서 추가한다 — 사람이 정한 것도 같은 기록에 남는 진화다.
 * 기계가 센 결과(이전 검증 경험 · 사례 유용도)는 글을 고칠 수 없다 — 끄거나 버릴 수만 있다.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiTrendingUp, FiDownload, FiPlus, FiEdit3, FiCheck, FiX, FiAlertCircle, FiExternalLink, FiZap, FiCopy } from 'react-icons/fi';
import AppModal from '../common/AppModal';
import { useConfirm } from '../ConfirmDialog';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';
import * as api from '../../services/experience';
import { AI_ENABLED } from '../../config/features';
import { writeClipboard } from '../../utils/gridCopy';
import { ExportPanel, ImportBar, LayerList, MergeView } from './ExperienceExchange';
import './ExperienceModal.css';
import memoWindow from '../common/memoWindow';

/** 화면의 묶음 — 상태로 가른다. 순서가 화면 순서 */
const GROUPS = [
  { id: 'proposed', statuses: ['proposed'] },
  { id: 'held', statuses: ['held'] },
  { id: 'active', statuses: ['active'] },
  { id: 'observing', statuses: ['observing'] },
  { id: 'off', statuses: ['retired'] },
  { id: 'discarded', statuses: ['discarded'] },
  { id: 'done', statuses: ['done'] },
];

function scopeText(scope, t) {
  const s = scope || {};
  const parts = [];
  if (s.connectionId) parts.push(t('common:evo.scope.connection', { name: s.connName || s.connectionId }));
  if (s.task) parts.push(t(`common:evo.task.${s.task}`, { defaultValue: s.task }));
  return parts.length ? parts.join(' · ') : t('common:evo.scope.all');
}

function evidenceText(it, t) {
  const ev = it.evidence || {};
  if (it.kind === 'pref') {
    if (ev.source === 'stated') return t(ev.via === 'user' ? 'common:evo.ev.byUser' : 'common:evo.ev.stated');
    return t('common:evo.ev.repeat', { count: ev.count || 0 });
  }
  if (it.kind === 'fix') {
    const base = t('common:evo.ev.fix', { tried: ev.tried || 0, improved: ev.improved || 0, failed: (ev.failed || 0) + (ev.invalid || 0) });
    // 사람이 한 일(채택·버림·고쳐 씀)이 있을 때만 덧붙인다 — 버린 사유는 고른 것만
    if (!(ev.adopted || ev.dropped || ev.edited)) return base;
    const why = Object.keys(ev.dropReasons || {}).map(k => `${t(`common:stn.dropReasons.${k}`, { defaultValue: k })} ${ev.dropReasons[k]}`).join(', ');
    return `${base} · ${t('common:evo.ev.fixActs', { adopted: ev.adopted || 0, dropped: ev.dropped || 0, edited: ev.edited || 0 })}${why ? ` (${why})` : ''}`;
  }
  if (it.kind === 'prop') return '';
  if (it.kind === 'proc') return t('common:evo.ev.proc', { count: ev.count || 0 });
  if (it.kind === 'nocode') return t('common:evo.ev.observeOnly');
  if (it.kind === 'gdev') return t('common:evo.ev.gdev', { deviated: ev.deviated || 0, adopted: ev.adoptedAfter || 0 });
  if (it.kind === 'case') return t('common:evo.ev.case', { shown: ev.shown || 0, useful: ev.useful || 0 });
  return '';
}

/**
 * 제안 한 건 — 기준(가이드·분류·사례)을 바꾸는 것은 사용자다. 여기서는 근거와 재료를 보이고 그 편집 화면을 연다.
 * 「반영함」 · 「버리기」 로 닫으면 새 근거가 쌓이기 전에는 다시 올라오지 않는다.
 */
function ProposalBody({ it, busy, t, onOpen, onDraft, onSaveDraft, drafting }) {
  const p = it.proposal || {};
  const [text, setText] = useState(p.draft || '');
  // 서버의 초안이 바뀌면(AI 가 새로 썼다) 부모가 key 를 바꿔 이 칸을 새로 만든다 — effect 로 맞추면 늦게 도는 effect 가 입력을 덮는다
  const canOpen = (p.type === 'case' && onOpen.job) || ((p.type === 'order' || p.type === 'gexc') && onOpen.guides) || (p.type === 'topic' && onOpen.cases);
  const open = () => {
    if (p.type === 'case') onOpen.job(p.jobId);
    else if (p.type === 'topic') onOpen.cases();
    else onOpen.guides();
  };
  return (
    <div className="exp-prop">
      {p.type === 'order' && p.builtin ? <div className="exp-propHint">{t('common:evo.prop.builtinHint')}</div> : null}
      {p.type === 'order' && p.guideTitle && !p.builtin ? <div className="exp-propHint">{t('common:evo.prop.guideHint', { title: p.guideTitle })}</div> : null}
      {p.type === 'topic' ? <div className="exp-propHint">{t('common:evo.prop.topicHint')}</div> : null}
      {(p.notes || []).length > 0 && (
        <ul className="exp-propList" aria-label={t('common:evo.prop.notes')}>
          {p.notes.map(n => <li key={n}>{n}</li>)}
        </ul>
      )}
      {(p.titles || []).length > 0 && (
        <ul className="exp-propList" aria-label={t('common:evo.prop.titles')}>
          {p.titles.map(n => <li key={n}>{n}</li>)}
        </ul>
      )}
      {p.type === 'gexc' && (text || p.draft) ? (
        <textarea className="exp-draft" rows={3} value={text} aria-label={t('common:evo.prop.draft')}
          onChange={e => setText(e.target.value)} onBlur={() => { if (text !== (p.draft || '')) onSaveDraft(it, text); }} />
      ) : null}
      <div className="exp-propActions">
        {canOpen ? <button type="button" className="app-btn app-btn-primary" disabled={busy} onClick={open}><FiExternalLink size={11} /> {t(`common:evo.prop.open.${p.type}`)}</button> : null}
        {p.type === 'gexc' && AI_ENABLED ? (
          <button type="button" className="app-btn" disabled={busy || drafting} onClick={() => onDraft(it)} title={t('common:evo.prop.aiDraftHint')}>
            <FiZap size={11} /> {drafting ? t('common:evo.prop.aiDrafting') : t('common:evo.prop.aiDraft')}
          </button>
        ) : null}
        {p.type === 'gexc' && text ? <button type="button" className="app-btn" onClick={async () => { if (await writeClipboard(text)) toast.success(t('common:evo.prop.copied'), { autoClose: 1200 }); }}><FiCopy size={11} /> {t('common:evo.prop.copy')}</button> : null}
      </div>
    </div>
  );
}
ProposalBody.propTypes = {
  it: PropTypes.object.isRequired, busy: PropTypes.bool, t: PropTypes.func.isRequired, onOpen: PropTypes.object.isRequired,
  onDraft: PropTypes.func.isRequired, onSaveDraft: PropTypes.func.isRequired, drafting: PropTypes.bool,
};

function ExperienceModal({ open, onClose, onOpenSqlTuningJob, onOpenTuningGuides, onOpenCases }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [limits, setLimits] = useState({ min: 4, max: 200, repeat: 3 });
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({ text: '', task: '' });
  const [editing, setEditing] = useState(null);        // { id, text }
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(null);     // AI 초안을 쓰는 중인 제안 id
  // 화면 — 목록 · 내보내기 · 병합(가져온 자료 한 층)
  const [view, setView] = useState({ mode: 'list' });
  const [extra, setExtra] = useState({ metrics: null, migration: null, layers: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.listItems();
      setItems(r.items || []);
      if (r.limits) setLimits(r.limits);
      setTasks(r.tasks || []);
      setExtra({ metrics: r.metrics || null, migration: r.migration || null, layers: r.layers || [] });
      // 창을 열어 본 것이 「알림을 봤다」 다 — 다음에는 새 소식 표시가 꺼진다(이번에는 그대로 보인다)
      if (r.notices) api.markSeen().catch(() => {});
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setLoading(false); }
  }, [t]);
  useEffect(() => { if (open) { setView({ mode: 'list' }); load(); } }, [open, load]);
  // 병합 화면에 넘기는 것은 고정한다 — 매 렌더 새 함수면 그 화면이 표를 다시 불러 고른 것이 사라진다
  const backToList = useCallback(() => setView({ mode: 'list' }), []);
  const afterMerge = useCallback(() => { setView({ mode: 'list' }); load(); }, [load]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await load(); } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(false); }
  };
  const add = () => run(async () => { await api.addPreference({ text: draft.text, task: draft.task || null }); setDraft({ text: '', task: '' }); });
  const patch = (id, body) => run(() => api.patchItem(id, body));
  const discard = async (it) => {
    const ok = await confirm({ title: t('common:evo.discardTitle'), message: t('common:evo.discardBody', { title: it.title }), confirmText: t('common:evo.discard'), variant: 'danger' });
    if (ok) patch(it.id, { discard: true });
  };
  // 가이드 예외의 초안 — AI 는 본인 계정으로, 누를 때만. 실패해도 제안은 그대로다(직접 적으면 된다)
  const draftWithAi = async (it) => {
    setDrafting(it.id);
    try { await api.draftProposal(it.id); await load(); } catch (err) { toast.error(formatApiError(err, t)); } finally { setDrafting(null); }
  };
  const onOpen = useMemo(() => ({ job: onOpenSqlTuningJob, guides: onOpenTuningGuides, cases: onOpenCases }), [onOpenSqlTuningJob, onOpenTuningGuides, onOpenCases]);
  const undoLayer = async (l) => {
    const ok = await confirm({ title: t('common:evo.xch.undoTitle'), message: t('common:evo.xch.undoBody', { label: l.label }), confirmText: t('common:evo.xch.undo'), variant: 'danger' });
    if (!ok) return;
    run(async () => {
      const r = await api.undoImport(l.id);
      toast.success(t('common:evo.xch.undone', { prefs: r.removed.prefs, guides: r.removed.guides }));
      if (r.keptGuides) toast.info(t('common:evo.xch.keptGuides', { count: r.keptGuides }));
    });
  };
  const saveEdit = () => run(async () => { await api.patchItem(editing.id, { text: editing.text }); setEditing(null); });

  const grouped = useMemo(() => GROUPS.map(g => ({ ...g, list: items.filter(x => g.statuses.includes(x.status)) })).filter(g => g.list.length), [items]);
  const draftLen = draft.text.trim().length;
  const draftOk = draftLen >= limits.min && draftLen <= limits.max;

  return (
    <AppModal open={open} onClose={onClose} title={t('common:evo.title')} icon={<FiTrendingUp size={14} />} className="exp-modal" windowed>
      <div className="exp-body">
        {view.mode === 'export' ? <ExportPanel onBack={backToList} /> : null}
        {view.mode === 'merge' ? <MergeView layerId={view.layerId} onBack={backToList} onDone={afterMerge} /> : null}
        {view.mode === 'list' ? (<>
        <div className="exp-intro">{t('common:evo.intro', { repeat: limits.repeat })}</div>
        {/* 효과 — 최근 30일의 수치만 보인다(좋아지고 있는지는 사용자가 판단한다) */}
        {extra.metrics && (extra.metrics.verify.total || extra.metrics.alts.adopted || extra.metrics.alts.dropped || extra.metrics.instructions) ? (
          <div className="exp-metrics" title={t('common:evo.metricsHint')}>
            {t('common:evo.metrics', {
              days: extra.metrics.days, verified: extra.metrics.verify.total, improved: extra.metrics.verify.improved,
              adopted: extra.metrics.alts.adopted, dropped: extra.metrics.alts.dropped, edited: extra.metrics.alts.edited,
              instructions: extra.metrics.instructions, withdrawn: extra.metrics.withdrawn,
            })}
          </div>
        ) : null}
        {extra.migration ? (
          <div className="exp-note">{t('common:evo.migrated', {
            at: storedTime(extra.migration.at, 'date'), converted: extra.migration.converted || 0, held: extra.migration.held || 0,
            lowered: extra.migration.lowered || 0, newer: extra.migration.newer || 0,
          })}</div>
        ) : null}
        <div className="exp-toolbar">
          <button type="button" className="app-btn" onClick={() => setView({ mode: 'export' })}><FiDownload size={12} /> {t('common:evo.xch.export')}</button>
          <ImportBar onImported={(layer) => { load(); setView({ mode: 'merge', layerId: layer.id }); }} />
        </div>

        {/* 직접 적는 선호 — 사람이 정한 것도 같은 기록에 남는 진화다. 바로 적용된다 */}
        <div className="exp-add">
          <input className="exp-input" value={draft.text} maxLength={limits.max} placeholder={t('common:evo.addPh')}
            aria-label={t('common:evo.addPh')} onChange={e => setDraft({ ...draft, text: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter' && draftOk && !busy) add(); }} />
          <select className="exp-select" value={draft.task} aria-label={t('common:evo.scopeLabel')} onChange={e => setDraft({ ...draft, task: e.target.value })}>
            <option value="">{t('common:evo.scope.all')}</option>
            {tasks.map(tk => <option key={tk} value={tk}>{t(`common:evo.task.${tk}`, { defaultValue: tk })}</option>)}
          </select>
          <button type="button" className="app-btn app-btn-primary" disabled={!draftOk || busy} onClick={add}><FiPlus size={12} /> {t('common:evo.add')}</button>
        </div>

        {loading && !items.length ? <div className="exp-empty">{t('common:state.loading')}</div> : null}
        {!loading && !items.length ? <div className="exp-empty">{t('common:evo.empty')}</div> : null}

        {grouped.map(g => (
          <section key={g.id} className={`exp-group exp-group-${g.id}`}>
            <h4 className="exp-groupTitle">
              {g.id === 'held' ? <FiAlertCircle size={12} /> : null}
              {t(`common:evo.group.${g.id}`)} <span className="exp-count">{g.list.length}</span>
            </h4>
            <div className="exp-groupHint">{t(`common:evo.groupHint.${g.id}`, { repeat: limits.repeat })}</div>
            <ul className="exp-list">
              {g.list.map(it => (
                <li key={it.id} className={`exp-item${it.notice ? ' exp-new' : ''}`}>
                  <div className="exp-main">
                    {editing && editing.id === it.id ? (
                      <span className="exp-editRow">
                        <input className="exp-input" value={editing.text} maxLength={limits.max} aria-label={t('common:evo.edit')}
                          onChange={e => setEditing({ ...editing, text: e.target.value })}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(null); }} />
                        <button type="button" className="app-btn" disabled={busy} onClick={saveEdit} title={t('common:action.save')} aria-label={t('common:action.save')}><FiCheck size={12} /></button>
                        <button type="button" className="app-btn" onClick={() => setEditing(null)} title={t('common:action.cancel')} aria-label={t('common:action.cancel')}><FiX size={12} /></button>
                      </span>
                    ) : <span className="exp-titleText">{it.title}</span>}
                    <span className="exp-meta">
                      <span className="exp-chip">{scopeText(it.scope, t)}</span>
                      <span className={`exp-chip exp-origin-${it.origin}`}>{t(`common:evo.origin.${it.origin}`, { defaultValue: it.origin })}{it.from ? ` · ${it.from}` : ''}</span>
                      <span className="exp-ev">{evidenceText(it, t)}</span>
                    </span>
                    {it.note ? <div className="exp-note">{it.note}</div> : null}
                    {it.kind === 'prop' && it.status === 'proposed' ? (
                      <ProposalBody key={`${it.id}:${(it.proposal && it.proposal.draft) || ''}`} it={it} busy={busy} t={t} onOpen={onOpen} drafting={drafting === it.id}
                        onDraft={draftWithAi} onSaveDraft={(x, text) => patch(x.id, { draft: text })} />
                    ) : null}
                    {it.readOnly ? <div className="exp-note">{t('common:evo.readOnly')}</div> : null}
                    {it.stale && it.status === 'active' ? <div className="exp-propHint">{t('common:evo.stale')}</div> : null}
                    {(it.lastFailures || []).length > 0 && (
                      <div className="exp-fails">
                        {it.lastFailures.map(f => (
                          <span key={`${f.at}-${f.verdict}`} className="exp-fail">
                            {storedTime(f.at, 'date')} · {t(`common:stn.verdicts.${f.verdict}`, { defaultValue: f.verdict })}{(f.tables || []).length ? ` · ${f.tables.join(', ')}` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="exp-actions">
                    {it.editable && it.status !== 'discarded' && !(editing && editing.id === it.id) && (
                      <button type="button" className="app-btn" disabled={busy} onClick={() => setEditing({ id: it.id, text: it.title })}><FiEdit3 size={11} /> {t('common:evo.edit')}</button>
                    )}
                    {it.kind === 'prop' && it.status === 'proposed' && <button type="button" className="app-btn" disabled={busy} onClick={() => patch(it.id, { done: true })}><FiCheck size={11} /> {t('common:evo.prop.done')}</button>}
                    {it.status === 'active' && <button type="button" className="app-btn" disabled={busy} onClick={() => patch(it.id, { enabled: false })}>{t('common:evo.turnOff')}</button>}
                    {!it.observeOnly && ['observing', 'held', 'retired', 'discarded'].includes(it.status) && (
                      <button type="button" className="app-btn" disabled={busy} onClick={() => patch(it.id, { enabled: true })}>{t('common:evo.turnOn')}</button>
                    )}
                    {!['discarded', 'done'].includes(it.status) && it.origin !== 'user' && <button type="button" className="app-btn app-btn-danger" disabled={busy} onClick={() => discard(it)}>{t('common:evo.discard')}</button>}
                    {it.origin === 'user' && <button type="button" className="app-btn app-btn-danger" disabled={busy} onClick={() => patch(it.id, { remove: true })}>{t('common:action.delete')}</button>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <LayerList layers={extra.layers} busy={busy}
          onOpen={(l) => setView({ mode: 'merge', layerId: l.id })}
          onToggle={(l, on) => run(() => api.setImportEnabled(l.id, on))}
          onUndo={undoLayer}
          onRemove={(l) => run(() => api.removeImport(l.id))} />
        </>) : null}
      </div>
    </AppModal>
  );
}

ExperienceModal.propTypes = {
  open: PropTypes.bool, onClose: PropTypes.func.isRequired,
  onOpenSqlTuningJob: PropTypes.func, onOpenTuningGuides: PropTypes.func, onOpenCases: PropTypes.func,
};
ExperienceModal.defaultProps = { open: false };

// 작업 탭을 오갈 때 App 이 새 콜백을 넘겨도 창을 통째로 다시 그리지 않는다(common/memoWindow, 2026-10-26 — 탭 전환이 멈칫했다)
export default memoWindow(ExperienceModal);
